// 行事曆狀態對帳（GDO-25、GDO-26，DESIGN.md §11.3）：純函式。
// 輸入快照中的遊戲與行事曆事件，算出每款遊戲在行事曆中的狀態、綁定建議與同日提示。
import { PLATFORMS } from '@dayone/core/dictionaries';
import { platformReleases } from '@dayone/core/platform-release';
import {
  compareReleaseDate,
  formatReleaseDate,
  GameStatus,
  parseReleaseDate,
  type ReleaseDate,
  releaseRange,
} from '@dayone/core/release-date';
import type { IndexRow } from '@dayone/core/snapshot';
import { normalizeForSearch } from '@dayone/core/text';

/** Day One 的事件（有 dayone 標記） */
export interface CalendarEvent {
  eventId: string;
  gameId: string;
  /** 活動第一天 */
  date: string;
  /** 活動最後一天（日期未確定的事件是一段期間，GDO-32） */
  end?: string;
  htmlLink: string;
  origin: 'created' | 'bound' | string;
  descHash: string;
  /** 建立或上次更新時採用的發售日（任何精度） */
  releaseDate?: string;
  precision?: string;
}

/** 事件代表的發售日：日期未確定的事件看記錄的發售日（活動第一天是加入當天），確定日期的看活動日期 */
export const eventRelease = (e: CalendarEvent) =>
  e.precision && e.precision !== 'day' && e.releaseDate ? e.releaseDate : e.date;

/** 行事曆中其他事件（綁定候選） */
export interface UnboundEvent {
  eventId: string;
  summary: string;
  date: string;
  htmlLink: string;
}

export type SyncState =
  /** 沒有事件 */
  | { kind: 'none'; date: ReleaseDate | null }
  /** 事件的發售日 = 目前發售日 */
  | { kind: 'synced'; event: CalendarEvent }
  /** 事件的發售日 ≠ 目前發售日（例如 10/30 → 10/23，或 2026 年 → 10/23） */
  | { kind: 'drift'; event: CalendarEvent; from: string; to: string }
  /** 有事件，但遊戲已取消或發售日變回未公布 */
  | { kind: 'orphan'; event: CalendarEvent };

/**
 * 事件應該是哪一天：選了平台時取這些平台中最早的日期（「我的平台」）；沒選或都不符合時用主要發售日。
 * 與伺服器的 calendarDate 相同規則（這裡用清單列，伺服器用詳情）。
 */
export function expectedDate(row: IndexRow, platforms: readonly string[]): ReleaseDate | null {
  const wanted = new Set(platforms.map((k) => PLATFORMS.findIndex((p) => p.key === k)));
  const dates = platformReleases(row)
    .filter((r) => !wanted.size || wanted.has(r.platform))
    .map((r) => r.date);
  if (dates.length) return [...dates].sort(compareReleaseDate)[0] as ReleaseDate;
  return parseReleaseDate(row.d);
}

export function syncState(row: IndexRow, event: CalendarEvent | undefined, platforms: readonly string[]): SyncState {
  const date = expectedDate(row, platforms);
  if (!event) return { kind: 'none', date };
  if (row.st === GameStatus.cancelled || !date || date.p === 'tba') return { kind: 'orphan', event };
  const to = formatReleaseDate(date);
  const from = eventRelease(event);
  return from === to ? { kind: 'synced', event } : { kind: 'drift', event, from, to };
}

/** 名稱比對用：去掉大小寫、全半形、標點、常見的版本後綴 */
const SUFFIX =
  /\b(remake|remastered|remaster|definitive|deluxe|ultimate|gold|premium|complete|collector'?s|edition|goty|game of the year)\b|重製版|重製|完全版|豪華版|終極版/giu;
export function normalizeTitle(s: string): string {
  return normalizeForSearch(s.replace(SUFFIX, ' ')).replaceAll(' ', '');
}

const DAY = 1;
const within = (a: string, b: ReleaseDate | null, days: number) => {
  const ra = parseReleaseDate(a);
  const x = ra ? releaseRange(ra) : null;
  const y = b ? releaseRange(b) : null;
  if (!x || !y) return false;
  // 模糊日期（例如 2026 Q4）：事件落在範圍內或前後 days 天內都算
  return x.start >= y.start - days * DAY && x.start <= y.end + days * DAY;
};

export interface Suggestion {
  event: UnboundEvent;
  row: IndexRow;
}

/**
 * 綁定建議（§11.5）：事件標題包含遊戲名稱或中文名（忽略大小寫、全半形、標點、Remake／重製版等後綴），
 * 而且日期在發售日前後 45 天內。太短的名稱（少於 4 個字元）不比對，避免誤判；每個事件只建議最符合的一款。
 */
export function suggestions(
  rows: readonly IndexRow[],
  unbound: readonly UnboundEvent[],
  dismissed: ReadonlySet<string>,
  boundGameIds: ReadonlySet<string>,
): Suggestion[] {
  const candidates = rows
    .filter((r) => !boundGameIds.has(r.id) && r.st !== GameStatus.cancelled)
    .map((r) => ({
      row: r,
      names: [r.n, r.z]
        .filter((n): n is string => !!n)
        .map(normalizeTitle)
        .filter((n) => n.length >= 4),
      date: parseReleaseDate(r.d),
    }))
    .filter((c) => c.names.length);
  const out: Suggestion[] = [];
  for (const event of unbound) {
    const title = normalizeTitle(event.summary);
    if (!title) continue;
    let best: { row: IndexRow; score: number } | null = null;
    for (const c of candidates) {
      if (dismissed.has(`${event.eventId}:${c.row.id}`)) continue;
      const hit = c.names.find((n) => title.includes(n));
      if (!hit || !within(event.date, c.date, 45)) continue;
      // 名稱越接近事件標題越優先（例如 Wolverine 與 Marvel's Wolverine）
      const score = hit.length / title.length;
      if (!best || score > best.score) best = { row: c.row, score };
    }
    if (best) out.push({ event, row: best.row });
  }
  return out;
}

/** 同一天的其他 Day One 事件數（「同日還有 N 款」） */
export function sameDayCount(events: readonly CalendarEvent[], gameId: string, date: string): number {
  return events.filter((e) => eventRelease(e) === date && e.gameId !== gameId).length;
}
